import { isPlatformBrowser, LocationStrategy, PathLocationStrategy } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withJsonpSupport,
} from '@angular/common/http';
import {
  APP_ID,
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
  PLATFORM_ID,
} from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { initializeAppCheck, provideAppCheck, ReCaptchaV3Provider } from '@angular/fire/app-check';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';
import { motorVuelosInterceptor } from './api/api-motor-vuelos/interceptors/motor-vuelos.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core';
import { GlobalHttpInterceptorService } from './core/interceptors/globalHttpInterceptorService.service';
import { LoadingInterceptor } from './core/interceptors/loading.interceptor';
import { MotorVuelosInterceptor } from './core/interceptors/motor-vuelos.interceptor';
import { CierrapuertasOfertasModule } from './public/cierrapuertas-ofertas/cierrapuertas-ofertas.module';
import { CierrapuertasModule } from './public/cierrapuertas/cierrapuertas.module';
import { CitaModule } from './public/cita/cita.module';
import { CondicionesDeReservaModule } from './public/condiciones-de-reserva/condiciones-de-reserva.module';
import { ConformidadModule } from './public/conformidad/conformidad.module';
import { DocumentacionViajeModule } from './public/documentacion-viaje/documentacion-viaje.module';
import { HomeModule } from './public/home/home.module';
import { NuestraEmpresaModule } from './public/nuestra-empresa/nuestra-empresa.module';
import { PoliticasModule } from './public/politicas/politicas.module';
import { ResponsabilidadSocialModule } from './public/resonsabilidad-social/responsabilidad-social.module';
import { RetailModule } from './public/retail/retail.module';
import { CryptoService } from './services/crypto/crypto.service';
import { ConfigCatService } from './services/labs';
import { PromoImageModalComponent } from './shared/components';
import { LoadingModule } from './shared/components/moleculas/loading/loading.module';
import { AdvertisementModalModule } from './shared/components/moleculas/modals';
import { NotificationModule } from './shared/components/moleculas/notification/notification.module';
import { PageNotFoundComponent } from './shared/components/moleculas/page-not-found/page-not-found.component';
import { PreFooterModule } from './shared/components/moleculas/pre-footer/pre-footer.module';
import { CookieBannerComponent } from './shared/components/organisms/cookie-banner';
import { CanActivateCheckoutGuard } from './shared/guards/checkout.guard';
import { DeactivatePaymentGuard } from './shared/guards/payment.guard';

export function initConfigCat(configCatService: ConfigCatService) {
  return () => configCatService.init();
}

export function initFirebaseAppCheck(platformId: Object) {
  return () => {
    if (isPlatformBrowser(platformId) && !environment.production)
      (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;

    return Promise.resolve();
  };
}

export function provideAppCheckFactory(platformId: Object) {
  return () => {
    if (isPlatformBrowser(platformId))
      return initializeAppCheck(getApp(), {
        provider: new ReCaptchaV3Provider(environment.reCaptchaSiteKey),
        isTokenAutoRefreshEnabled: true,
      });

    return null as unknown as ReturnType<typeof initializeAppCheck>;
  };
}

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  declarations: [AppComponent, PageNotFoundComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    NotificationModule,
    LoadingModule,
    NgbTooltipModule,
    AdvertisementModalModule,
    PreFooterModule,
    CitaModule,
    HomeModule,
    ConformidadModule,
    DocumentacionViajeModule,
    CierrapuertasModule,
    CierrapuertasOfertasModule,
    RetailModule,
    NuestraEmpresaModule,
    PoliticasModule,
    CondicionesDeReservaModule,
    ResponsabilidadSocialModule,
    CoreModule,
    PromoImageModalComponent,
    CookieBannerComponent,
  ],
  providers: [
    { provide: APP_ID, useValue: 'nmviajes' },
    provideClientHydration(),
    provideHttpClient(withFetch(), withJsonpSupport(), withInterceptors([motorVuelosInterceptor])),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideAppCheck(() => provideAppCheckFactory as any),
    {
      provide: APP_INITIALIZER,
      useFactory: initConfigCat,
      deps: [ConfigCatService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initFirebaseAppCheck,
      deps: [PLATFORM_ID],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: GlobalHttpInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MotorVuelosInterceptor, multi: true },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    CryptoService,
    CanActivateCheckoutGuard,
    DeactivatePaymentGuard,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
